import React from 'react';
import { Sparkle, CheckCircle, ArrowRight } from '@phosphor-icons/react';

/**
 * Reusable Profile Completeness card.
 * Used by both Worker and Contractor Control Panels.
 *
 * Props:
 *  - percent: 0-100 integer
 *  - missingFields: [{ key, label }] — fields still empty
 *  - onComplete: () => void — usually switches to the Profile tab
 *  - rewardCopy?: optional override for the 100% reward message
 */
export default function ProfileCompletenessCard({
  percent = 0,
  missingFields = [],
  onComplete,
  rewardCopy = "Profile is 100% complete — clients see you first!",
}) {
  const tier = percent >= 80 ? 'good' : percent >= 50 ? 'warn' : 'low';
  const palette = {
    good: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', bar: 'bg-emerald-500', label: 'Looking great' },
    warn: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', bar: 'bg-amber-500', label: 'Almost there' },
    low:  { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-400', bar: 'bg-rose-500', label: 'Get noticed' },
  }[tier];

  return (
    <div
      className={`border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] ${palette.bg}`}
      data-testid="profile-completeness-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg border-2 border-black flex items-center justify-center bg-white dark:bg-neutral-800 flex-shrink-0">
            <Sparkle size={22} weight="fill" className={palette.text} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
              Profile Completeness · {palette.label}
            </p>
            <p className={`text-2xl font-black ${palette.text}`} data-testid="profile-completeness-percent">
              {percent}%
            </p>
          </div>
        </div>
        {percent < 100 && (
          <button
            onClick={onComplete}
            className="flex items-center gap-1.5 h-10 px-4 bg-[#2B4C6F] text-white font-bold text-sm rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex-shrink-0"
            data-testid="complete-profile-cta"
          >
            Complete <ArrowRight size={14} weight="bold" />
          </button>
        )}
      </div>

      <div className="h-3 w-full bg-white dark:bg-neutral-700 border-2 border-black overflow-hidden rounded-sm">
        <div
          className={`h-full ${palette.bar} transition-all duration-500 ease-out`}
          style={{ width: `${percent}%` }}
          data-testid="profile-completeness-bar"
        />
      </div>

      {missingFields.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
            Add to reach 100%
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="profile-completeness-missing">
            {missingFields.map((f) => (
              <span
                key={f.key}
                className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-bold rounded-full border border-black bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                data-testid={`missing-${f.key}`}
              >
                + {f.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {percent === 100 && (
        <p className="mt-3 text-sm font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
          <CheckCircle size={16} weight="fill" />
          {rewardCopy}
        </p>
      )}
    </div>
  );
}
